import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { ChevronDown, LogOut, Menu } from "lucide-react";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { GarageFlowLogo } from "@/components/Brand";
import { useAuth } from "@/context/AuthContext";
import { t } from "@/i18n";

const KEY = "gf_nav_collapsed";
const readCollapsed = () => { try { return JSON.parse(localStorage.getItem(KEY) || "{}"); } catch { return {}; } };

export function isItemActive(item, loc) {
  const [path, query] = item.to.split("?");
  if (item.end ? loc.pathname !== path : !(loc.pathname === path || loc.pathname.startsWith(`${path}/`))) return false;
  if (query) return [...new URLSearchParams(query)].every(([k, v]) => new URLSearchParams(loc.search).get(k) === v);
  return !item.exclusive || !Object.entries(item.exclusive).some(([k, v]) => new URLSearchParams(loc.search).get(k) === v);
}

function NavItem({ item, onNavigate, compact }) {
  const loc = useLocation();
  const active = isItemActive(item, loc);
  const Icon = item.icon;
  return (
    <NavLink to={item.to} onClick={onNavigate} data-testid={item.testId}
      className={`flex items-center gap-3 rounded-lg px-3 text-sm font-medium ${compact ? "min-h-8 py-1" : "min-h-10 py-2"} transition-colors duration-150 ${active ? "bg-teal-400/15 text-white shadow-[inset_3px_0_0_#2dd4bf]" : "text-slate-300 hover:bg-white/5 hover:text-white"}`}>
      {Icon && <Icon className="h-4 w-4 shrink-0" />}<span className="truncate">{item.label}</span>
      {item.badge > 0 && <span className="ml-auto rounded-full bg-amber-400 px-2 text-xs font-bold text-slate-900" data-testid={`${item.testId}-badge`}>{item.badge}</span>}
    </NavLink>
  );
}

function NavGroup({ g, collapsed, toggle, onNavigate }) {
  const loc = useLocation();
  if (!g.label) return <div className="space-y-0.5">{g.items.map((i) => <NavItem key={i.to} item={i} onNavigate={onNavigate} />)}</div>;
  const hasActive = g.items.some((i) => isItemActive(i, loc));
  const open = hasActive || !collapsed;
  const Icon = g.icon;
  return (
    <div>
      <button type="button" onClick={() => toggle(g.key)} aria-expanded={open} data-testid={`nav-group-${g.key}`}
        className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-[11px] font-bold uppercase tracking-[0.16em] text-slate-400 transition-colors duration-150 hover:text-white">
        {Icon && <Icon className="h-3.5 w-3.5" />}{g.label}<ChevronDown className={`ml-auto h-3.5 w-3.5 transition-transform duration-200 ${open ? "" : "-rotate-90"}`} />
      </button>
      {open && <div className="mt-0.5 space-y-0.5 pl-2">{g.items.map((i) => <NavItem key={i.to} item={i} onNavigate={onNavigate} />)}</div>}
    </div>
  );
}

function SidebarBody({ groups, bottom = [], top, onNavigate, homeTo }) {
  const { user, logout } = useAuth();
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const toggle = (k) => setCollapsed((c) => { const n = { ...c, [k]: !c[k] }; localStorage.setItem(KEY, JSON.stringify(n)); return n; });
  return (
    <div className="flex h-full min-h-0 flex-col bg-[#0A2540]">
      <div className="min-h-0 flex-1 space-y-5 overflow-y-auto overscroll-contain p-4 [-webkit-overflow-scrolling:touch]" data-testid="sidebar-scroll">
        <div className="px-1"><GarageFlowLogo light to={homeTo} /></div>
        {top}
        <nav className="space-y-3" data-testid="sidebar-nav">{groups.filter((g) => g.items.length).map((g) => <NavGroup key={g.key} g={g} collapsed={!!collapsed[g.key]} toggle={toggle} onNavigate={onNavigate} />)}</nav>
      </div>
      <div className="shrink-0 space-y-0 border-t border-white/10 px-4 pt-2 pb-[calc(env(safe-area-inset-bottom)+0.75rem)]" data-testid="sidebar-footer">
        {bottom.map((i) => <NavItem key={i.to} item={i} onNavigate={onNavigate} compact />)}
        <div className="flex items-center gap-2 px-3 pt-2">
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-semibold text-white" data-testid="current-user-name">{user?.name}</p>
            <p className="truncate text-xs text-slate-400" data-testid="current-user-email">{user?.email}</p>
          </div>
          <Button variant="ghost" size="icon" onClick={logout} data-testid="logout-button" aria-label={t("nav.logout")} className="text-slate-300 hover:bg-white/10 hover:text-white"><LogOut className="h-4 w-4" /></Button>
        </div>
      </div>
    </div>
  );
}

function TabBar({ tabs, onMore }) {
  const loc = useLocation();
  return (
    <nav className="fixed inset-x-0 bottom-0 z-40 grid border-t border-slate-200 bg-white/95 pb-[env(safe-area-inset-bottom)] backdrop-blur lg:hidden" style={{ gridTemplateColumns: `repeat(${tabs.length + 1}, 1fr)` }} data-testid="mobile-tab-bar">
      {tabs.map((tb) => {
        const active = (tb.match || [tb.to]).some((p) => isItemActive({ to: p, end: tb.end }, loc));
        const Icon = tb.icon;
        return (
          <Link key={tb.to} to={tb.to} data-testid={tb.testId} className={`flex flex-col items-center gap-1 py-2.5 text-[11px] font-semibold transition-colors duration-150 ${active ? "text-[#0A2540]" : "text-slate-400"}`}>
            <Icon className="h-5 w-5" />{tb.label}
          </Link>
        );
      })}
      <button type="button" onClick={onMore} data-testid="mobile-menu-button" className="flex flex-col items-center gap-1 py-2.5 text-[11px] font-semibold text-slate-400"><Menu className="h-5 w-5" />{t("b13.more")}</button>
    </nav>
  );
}

export function AppShell({ groups, bottom, top, banners, children, homeTo, mobileTitle, actions, tabs = [], fab }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="min-h-screen bg-slate-50 lg:grid lg:grid-cols-[260px_1fr]">
      <aside className="sticky top-0 hidden h-[100dvh] lg:block">
        <SidebarBody groups={groups} bottom={bottom} top={top} homeTo={homeTo} />
      </aside>
      <div className="flex min-w-0 flex-col">
        <header className="sticky top-0 z-30 flex items-center gap-2 border-b border-slate-200 bg-white/90 px-4 py-2.5 backdrop-blur lg:justify-end lg:px-10" data-testid="top-bar">
          <span className="lg:hidden"><GarageFlowLogo to={homeTo} testId="mobile-logo" /></span>
          <span className="mx-2 min-w-0 flex-1 truncate text-right text-sm font-semibold text-slate-700 lg:hidden">{mobileTitle}</span>
          <div className="flex items-center gap-2 lg:gap-3">{actions}</div>
        </header>
        {banners}
        <main className="mx-auto w-full max-w-7xl flex-1 px-4 pt-6 pb-28 sm:px-6 lg:px-10 lg:py-8">{children}</main>
      </div>
      <TabBar tabs={tabs} onMore={() => setOpen(true)} />
      {fab && <div className="fixed right-4 bottom-[calc(env(safe-area-inset-bottom)+4.75rem)] z-40 lg:hidden">{fab}</div>}
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="left" className="h-[100dvh] max-h-[100dvh] w-[290px] overflow-hidden border-0 p-0">
          <SheetTitle className="sr-only">{t("nav.menu")}</SheetTitle>
          <SidebarBody groups={groups} bottom={bottom} top={top} onNavigate={() => setOpen(false)} homeTo={homeTo} />
        </SheetContent>
      </Sheet>
    </div>
  );
}
